// @angular
import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
// Translation service
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { NotificationModalService } from "../../../modules/notification-module/notification-service/notification-modal.service";
import { ThemeService } from "../../../../app/themes/theme.service";
import { NavbarMenuService } from "../navbar-side/navbar-menu.service";
// Interfaces
import { Client } from "../../../interfaces/ClientGlobalInterface.type";
import { Entity } from "../../../interfaces/EntityGlobalInterface.type";
import { Agrupation } from "../../../interfaces/AgrupationGlobalInterface.type";
import { MenuItem } from "../navbar-side/navbar-menu";
// Variables
import { PROFILES } from "../../../../assets/profiles/profiles";
import { LANGUAGE } from "../../../services/language/LanguageController.service";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { FormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { NgClass } from "@angular/common";
import { ENVIRONMENT } from "../../../../environments/environment";
import { NavbarTopSearchNavigationComponent } from "../navbar-top/navbar-top-search-navigation/navbar-top-search-navigation.component";
import { isPresentationModeRestrictedRoute } from "../../../services/shared/PresentationModeGuard.service";

/**
 * Responsive navigation: scope pickers, device and CUPS search, preferences, notifications, logout, and the ARSON presentation-mode toggle.
 * Session lists and current selections come as inputs; changes, search selection, logout and language change are emitted as flags.
 */
@Component({
  selector: "app-navbar-responsive",
  templateUrl: "./navbar-responsive.component.html",
  styleUrls: ["./navbar-responsive.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    RouterLink,
    FormsModule,
    ScrollingModule,
    TranslateModule,
    NavbarTopSearchNavigationComponent
  ]
})
export class NavbarResponsiveComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Session variables
  @Input() sessionProfile: string;
  @Input() sessionUserName: { name: string; surname: string };
  @Input() currentClient: Client;
  @Input() currentEntity: Entity;
  @Input() currentAgrupation: Agrupation;
  @Input() clientList: Client[];
  @Input() entityList: Entity[];
  @Input() agrupationList: Agrupation[];
  currentUserId: number;
  readonly PROFILES = PROFILES;
  readonly LANGUAGE = LANGUAGE;
  readonly ENVIRONMENT = ENVIRONMENT;

  // Navigation control
  componentInitiated: boolean = false;
  activeMenu: string;
  dropdownActive = "";
  languageMenu: boolean = false;
  themeMenu: boolean = false;
  searchActive: string = null;
  cupsSelected: boolean = false;
  searchOptionSelected: string = this.translate.instant("meter-search");
  @Output() showResponsiveMenuFlag = new EventEmitter<any>();
  @Output() logoutFlag = new EventEmitter<any>();
  @Output() languageChangeFlag = new EventEmitter<string>();

  // Search variables
  @Input()
  get meterList(): any[] {
    return this._meterList;
  }
  set meterList(meterList: any[]) {
    this._meterList = meterList;
    if (!this.cupsSelected && this.componentInitiated) {
      this.setSearchDropdownHeight();
    }
  }
  _meterList: any[];
  @Input()
  get cupsList(): any[] {
    return this._cupsList;
  }
  set cupsList(cupsList: any[]) {
    this._cupsList = cupsList;
    if (this.cupsSelected && this.componentInitiated) {
      this.setSearchDropdownHeight();
    }
  }
  _cupsList: any[];
  @Input() currentCupsName: string;
  @Input() currentCupsConf: any;
  @Input() pendingNotifications: number;
  @Output() resetFilterFlag = new EventEmitter<any>();
  @Output() searchSelection = new EventEmitter<any>();
  @Output() dropdownFilterFlag = new EventEmitter<any>();
  @Output() updateClientFlag = new EventEmitter<any>();
  @Output() updateEntityFlag = new EventEmitter<any>();
  @Output() updateAgrupationFlag = new EventEmitter<any>();
  @Output() deleteUserLocalSettingsFlag = new EventEmitter<any>();
  @Output() resetMapSettingsFlag = new EventEmitter<void>();
  @ViewChild("responsiveOptionsDropdown") responsiveOptionsDropdown: ElementRef;
  @ViewChild("responsiveSearchFilter") responsiveSearchFilter: ElementRef;
  @ViewChild("responsiveClientFilter") responsiveClientFilter: ElementRef;
  @ViewChild("responsiveEntityFilter") responsiveEntityFilter: ElementRef;
  @ViewChild("responsiveAgrupationFilter")
  responsiveAgrupationFilter: ElementRef;
  @ViewChild("responsiveSearchDropdown") responsiveSearchDropdown: ElementRef;
  @ViewChild("responsiveClientDropdown") responsiveClientDropdown: ElementRef;
  @ViewChild("responsiveEntityDropdown") responsiveEntityDropdown: ElementRef;
  @ViewChild("responsiveAgrupationDropdown")
  responsiveAgrupationDropdown: ElementRef;
  @ViewChild("responsiveSearchBoxDropdown")
  responsiveSearchBoxDropdown: ElementRef;
  @ViewChild("searchDeviceResponsiveButton")
  searchDeviceResponsiveButton: ElementRef;

  // Menu
  menuList: MenuItem[];
  menuListBy3: MenuItem[][];

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly navbarMenu: NavbarMenuService,
    public notificationModal: NotificationModalService,
    private readonly SessionDataService: SessionDataService,
    private readonly theme: ThemeService,
    private readonly translate: TranslateService,
    private readonly router: Router
  ) {
    effect(() => {
      const navbarMenu = this.SessionDataService.navbarMenu();
      if (navbarMenu !== null) {
        this.menuList = navbarMenu;
        this.chunkMenuList();
      }
    });
    effect(() => {
      if (this.SessionDataService.presentationModeActive()) {
        this.cupsSelected = false;
        this.searchOptionSelected = this.translate.instant("meter-search");
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentUserId = untracked(() => this.SessionDataService.userId());
    this.navbarMenu.loadMenu();
    this.menuList = untracked(() => this.SessionDataService.navbarMenu());
    this.chunkMenuList();

    this.componentInitiated = true;
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Methods
  /***************************************************************************/

  // Build menu
  chunkMenuList(): void {
    let menuListBy3: MenuItem[][] = [];
    let chunkSize = 3;

    for (let i = 0; i < this.menuList.length; i += chunkSize) {
      let chunk = this.menuList.slice(i, i + chunkSize);
      menuListBy3.push(chunk);
    }

    setTimeout(() => {
      this.menuListBy3 = menuListBy3;
    }, 0);
  }

  // Hide menu
  hideResponsiveMenu(): void {
    this.activeMenu = "";
    this.showResponsiveMenuFlag.emit();
  }

  // Logout
  logout(): void {
    this.logoutFlag.emit();
  }

  // Notify parent of language change
  switchLanguage(newLanguage: string): void {
    this.languageChangeFlag.emit(newLanguage);
  }

  // Search box text
  searchSelect(searchOption: string): void {
    if (searchOption === "cups" && this.presentationModeActive) {
      return;
    }
    if (searchOption === "device") {
      this.cupsSelected = false;
      this.searchOptionSelected = this.translate.instant("meter-search");
    } else {
      this.cupsSelected = true;
      this.searchOptionSelected = this.translate.instant("search-of") + " " + this.currentCupsName;
    }
  }

  get presentationModeActive(): boolean {
    return this.SessionDataService.presentationModeActive();
  }

  togglePresentationMode(): void {
    const activating = !this.SessionDataService.presentationModeActive();
    this.SessionDataService.togglePresentationMode();
    if (activating && isPresentationModeRestrictedRoute(this.router.url)) {
      void this.router.navigateByUrl("/principal");
    }
  }

  // Reset all search boxes
  resetAllSearchBox(): void {
    this.resetSearchBox(this.responsiveAgrupationFilter, "agrupation");
    this.resetSearchBox(this.responsiveEntityFilter, "entity");
    this.resetSearchBox(this.responsiveClientFilter, "client");
    this.resetSearchBox(this.responsiveSearchFilter, "search");
  }

  // Notify parent of reset
  resetSearchBox(filter: any, type: string): void {
    filter.value = "";
    this.resetFilterFlag.emit(type);
  }

  // Notify parent of selection
  goToSelection(cupsSelected: boolean, selection: any): void {
    this.searchSelection.emit({
      cupsSelected: cupsSelected,
      selection: selection
    });
  }

  // Close dropdown on click
  closeDropdown(dropdown: any): void {
    dropdown.classList.remove("responsive-nav-dropdown-active");
    this.searchActive = null;
  }

  // Open dropdown on click
  openDropdown(dropdown: any): void {
    dropdown.classList.add("responsive-nav-dropdown-active");
  }

  dropdownFilter(filterValue: any, type: string, cupsSelected?: boolean): void {
    this.dropdownFilterFlag.emit({
      filterValue: filterValue,
      type: type,
      cupsSelected: cupsSelected
    });
  }

  updateCurrentAgrupation(agrupation: any): void {
    this.updateAgrupationFlag.emit(agrupation);
  }

  updateCurrentEntity(entity: any): void {
    this.updateEntityFlag.emit(entity);
  }

  updateCurrentClient(client: any): void {
    this.updateClientFlag.emit(client);
  }

  resetSearchDevice(): void {
    this.searchDeviceResponsiveButton?.nativeElement.click();
  }

  // Set search box height
  setSearchDropdownHeight(): void {
    let searchList: any[] = this.cupsSelected ? this.cupsList : this.meterList;
    if (searchList?.length < 10) {
      this.responsiveSearchDropdown.nativeElement.style.setProperty(
        "--search-height",
        searchList.length
      );
    } else {
      this.responsiveSearchDropdown.nativeElement.style.setProperty("--search-height", 10);
    }
  }

  // Delete local settings
  deleteUserLocalSettings(): void {
    this.deleteUserLocalSettingsFlag.emit();
  }

  // Reset map settings to default values
  resetMapSettings(): void {
    this.resetMapSettingsFlag.emit();
  }

  // Update style theme
  updateTheme(theme: string): void {
    this.theme.setTheme(theme);
    this.theme.saveThemeConfig(theme);
  }
}
